import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import Modal from "react-modal";
import LabsButton from "./LabsButton.jsx";
import ModalTermsOfService from "./Modal/ModalTermsOfService.jsx";
import ModalPrivacyPolicy from "./Modal/ModalPrivacyPolicy.jsx";

import Ask from "./Ask.jsx";
import AskMobile from "./AskMobile.jsx";
import useStore from "../store.js";
import logo from "../img/logo.png";
import guestLoginLock from "../img/guest_login_lock.png";
import googlePlayBadge from "../img/badge_google_play.png";
import appStoreBadge from "../img/badge_app_store.png";
import {
  isUnavailableGuestSession,
  prepareGuestChatLogin,
  prepareUnavailableGuestChatLogin,
} from "../utils/guestChatHandoff.js";

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6.75 6.75L17.25 17.25" />
    <path d="M17.25 6.75L6.75 17.25" />
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285f4"
      d="M21.6 12.23c0-.71-.06-1.4-.18-2.05H12v3.87h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.35Z"
    />
    <path
      fill="#34a853"
      d="M12 22c2.7 0 4.97-.9 6.62-2.42l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.6 0-4.81-1.76-5.6-4.13H3.06v2.6A10 10 0 0 0 12 22Z"
    />
    <path
      fill="#fbbc05"
      d="M6.4 13.9A6 6 0 0 1 6.1 12c0-.66.11-1.3.3-1.9V7.5H3.06A10 10 0 0 0 2 12c0 1.61.39 3.14 1.06 4.5l3.34-2.6Z"
    />
    <path
      fill="#ea4335"
      d="M12 5.97c1.47 0 2.79.5 3.82 1.5l2.87-2.87A9.61 9.61 0 0 0 12 2a10 10 0 0 0-8.94 5.5l3.34 2.6c.79-2.37 3-4.13 5.6-4.13Z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#000"
      d="M17.05 12.54c-.03-2.63 2.15-3.9 2.25-3.97a4.84 4.84 0 0 0-3.8-2.06c-1.6-.17-3.13.96-3.95.96-.83 0-2.08-.94-3.44-.91a5.03 5.03 0 0 0-4.25 2.59c-1.84 3.2-.47 7.91 1.3 10.52.87 1.27 1.87 2.67 3.22 2.62 1.3-.05 1.8-.84 3.38-.84 1.56 0 2.02.84 3.39.81 1.41-.02 2.3-1.26 3.13-2.53a10.7 10.7 0 0 0 1.42-2.95 4.56 4.56 0 0 1-2.65-4.24Zm-2.6-7.72A4.6 4.6 0 0 0 15.48 1.6a4.63 4.63 0 0 0-3 1.54 4.34 4.34 0 0 0-1.07 3.08 3.9 3.9 0 0 0 3.04-1.4Z"
    />
  </svg>
);

const NaverIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#03c75a" d="M4 3.5h5.1l5.8 8.3V3.5H20v17h-5.1l-5.8-8.3v8.3H4v-17Z" />
  </svg>
);

const KakaoIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#3c1e1e"
      d="M12 3.5c-5.52 0-10 3.56-10 7.95 0 2.84 1.86 5.33 4.67 6.74l-.95 3.48c-.08.3.26.54.52.37l4.14-2.74c.53.07 1.08.1 1.62.1 5.52 0 10-3.56 10-7.95S17.52 3.5 12 3.5Z"
    />
  </svg>
);

const providers = [
  { id: "google", label: "Google로 계속하기", Icon: GoogleIcon },
  { id: "apple", label: "Apple로 계속하기", Icon: AppleIcon },
  { id: "naver", label: "네이버로 계속하기", Icon: NaverIcon },
  { id: "kakao", label: "카카오로 계속하기", Icon: KakaoIcon },
];

const MOBILE_MAX_WIDTH = 579;

const GuestChatExperience = ({ mobile = false, initialTextValue = "", sharedDraftRef, onProviderLogin, loginState, onLoginStateChange }) => {
  const {
    isChatMode,
    guestHandoffToken,
    guestLoginReason,
    guestSessionComplete,
    isAIResponding,
    closeGuestLogin,
    fetchGuestChatAvailability,
    resetGuestChatState,
    syncGuestIssues,
    chatHistory,
  } = useStore();
  const { intent: loginIntent, preparing: isPreparingLogin, error: loginError } = loginState;
  const isOAuthOpen = loginIntent !== null;
  const [openPolicy, setOpenPolicy] = useState(null);
  const [screenWidth, setScreenWidth] = useState(() => window.innerWidth);
  const localDraftRef = useRef("");
  const draftRef = sharedDraftRef || localDraftRef;
  const [entryDraft, setEntryDraft] = useState(() => sharedDraftRef?.current ?? initialTextValue);
  const initialTextRef = useRef(initialTextValue);
  const rememberDraft = useCallback((value) => { draftRef.current = value; }, [draftRef]);
  useEffect(() => {
    if (initialTextRef.current === initialTextValue) return;
    initialTextRef.current = initialTextValue;
    setEntryDraft(initialTextValue);
  }, [initialTextValue]);
  const hidesMobileChatHeader = (
    mobile && isChatMode && screenWidth <= MOBILE_MAX_WIDTH
  );

  const updateLogin = (changes) => onLoginStateChange((current) => ({ ...current, ...changes }));
  const openOAuth = (intent = "continue") => updateLogin({ intent, error: "", unavailableSession: null });

  const startProviderLogin = async (provider) => {
    if (isPreparingLogin) return;
    if (isUnavailableGuestSession(loginState.unavailableSession?.status) &&
      loginState.unavailableSession.handoffToken === guestHandoffToken) {
      if (!prepareUnavailableGuestChatLogin(loginState.unavailableSession)) {
        updateLogin({ error: "입력한 내용을 보관하지 못했습니다. 내용을 복사한 뒤 다시 시도해주세요." });
        return;
      }
      updateLogin({ preparing: true });
      onProviderLogin(provider);
      return;
    }
    if (isChatMode && isAIResponding && !guestHandoffToken) {
      updateLogin({ error: "AI 답변을 마무리하고 있습니다. 잠시 후 다시 시도해주세요." });
      return;
    }

    updateLogin({ preparing: true, error: "" });
    const synced = guestHandoffToken ? await syncGuestIssues() : true;
    if (synced?.updated === false && isUnavailableGuestSession(synced.status)) {
      const draft = draftRef.current.trim()
        ? draftRef.current
        : (chatHistory || []).find((message) => message.type === "user")?.content || "";
      updateLogin({
        preparing: false,
        unavailableSession: { status: synced.status, draft, handoffToken: guestHandoffToken },
        error: `${synced.status === "guest_session_expired" ? "비로그인 대화의 저장 기간이 지나" : "비로그인 대화를 찾을 수 없어"} 이전 대화를 이어올 수 없습니다. 로그인 방법을 다시 선택하면 로그인만 진행합니다.${draft ? " 작성한 내용은 새 대화의 초안으로 남깁니다." : ""}`,
      });
      return;
    }
    if (synced !== true) {
      updateLogin({ preparing: false, error: "법률 쟁점을 저장하지 못했습니다. 잠시 후 다시 시도해주세요." });
      return;
    }

    prepareGuestChatLogin({
      handoffToken: guestHandoffToken,
      intent: loginIntent,
    });
    onProviderLogin(provider);
  };

  const returnToGuestHome = async () => {
    resetGuestChatState();
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    await fetchGuestChatAvailability();
  };

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [isChatMode]);

  useEffect(() => {
    if (!mobile) return undefined;

    const updateScreenWidth = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", updateScreenWidth);
    return () => window.removeEventListener("resize", updateScreenWidth);
  }, [mobile]);

  const header = (
    <GuestHeader $mobile={mobile}>
      <LogoButton
        $mobile={mobile}
        type="button"
        aria-label="로우로우 첫 화면"
        onClick={returnToGuestHome}
      >
        <LogoImage src={logo} alt="Lawlow" />
      </LogoButton>
      <HeaderLoginGroup $mobile={mobile}>
        <LabsButton />
        <HeaderLoginButton type="button" onClick={() => openOAuth("continue")}>
          간편 로그인
        </HeaderLoginButton>
      </HeaderLoginGroup>
    </GuestHeader>
  );

  return (
    <>
      <Experience data-behavior="landing-chat" data-guest-experience $mobile={mobile} $isChatMode={isChatMode}>
        {(!mobile || !isChatMode) && header}
        {mobile ? (
          <MobileAskArea $isChatMode={isChatMode}>
            <AskMobile
              guestMode
              initialTextValue={entryDraft}
              guestHeader={isChatMode && !hidesMobileChatHeader ? header : null}
              onDraftChange={rememberDraft}
              draftRef={draftRef}
            />
            {hidesMobileChatHeader && (
              <MobileChatCloseButton
                type="button"
                aria-label="비로그인 채팅 닫기"
                onClick={returnToGuestHome}
              >
                <CloseIcon />
              </MobileChatCloseButton>
            )}
          </MobileAskArea>
        ) : (
          <>
            <DesktopAskArea $isChatMode={isChatMode}>
              <Ask guestMode initialTextValue={entryDraft} onDraftChange={rememberDraft} draftRef={draftRef} />
            </DesktopAskArea>
            {!isChatMode && (
              <DesktopStoreLinks aria-label="로우로우 앱 다운로드">
                <a
                  href="https://play.google.com/store/apps/details?id=com.lawlow.lawlow_app&pli=1"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={googlePlayBadge} alt="Google Play에서 받기" />
                </a>
                <a
                  href="https://apps.apple.com/kr/app/%EB%A1%9C%EC%9A%B0%EB%A1%9C%EC%9A%B0/id6748278644"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={appStoreBadge} alt="App Store에서 받기" />
                </a>
              </DesktopStoreLinks>
            )}
          </>
        )}
      </Experience>

      {guestLoginReason && !isOAuthOpen && (
        <Overlay
          aria={{ modal: true, labelledby: "guest-login-required-title" }}
          onRequestClose={closeGuestLogin}
        >
          <RequiredCard data-behavior="login-required">
            <CloseButton
              type="button"
              onClick={closeGuestLogin}
              aria-label="로그인 안내 닫기"
            >
              <CloseIcon />
            </CloseButton>
            <LockImage src={guestLoginLock} alt="" />
            <RequiredContent>
              <RequiredTitle id="guest-login-required-title">
                {guestLoginReason === "search"
                  ? "판례 검색은 로그인 후 이용할 수 있어요"
                  : guestSessionComplete
                    ? "무료 대화 세션 1회를 모두 이용했어요"
                    : "서비스를 계속 이용하시려면"}
              </RequiredTitle>
              <RequiredDescription>
                로그인하면 지금 대화와 법률 쟁점을 이어가고
                <br />
                실제 판례 검색을 이용할 수 있어요.
              </RequiredDescription>
              <ContinueButton
                type="button"
                onClick={() => openOAuth(guestLoginReason)}
              >
                로그인 후 {guestLoginReason === "search" ? "검색 진행하기" : "계속 이용하기"}
              </ContinueButton>
            </RequiredContent>
          </RequiredCard>
        </Overlay>
      )}

      {isOAuthOpen && (
        <Overlay
          aria={{ modal: true, labelledby: "guest-oauth-title" }}
          onRequestClose={() => updateLogin({ intent: null })}
        >
          <OAuthCard data-behavior="login-dialog">
            <CloseButton
              type="button"
              onClick={() => updateLogin({ intent: null })}
              aria-label="간편 로그인 닫기"
            >
              <CloseIcon />
            </CloseButton>
            <OAuthTitle id="guest-oauth-title">간편 로그인</OAuthTitle>
            <OAuthDescription>
              가입하면 판례 검색 1회 무료
            </OAuthDescription>
            {loginError && <OAuthError role="alert">{loginError}</OAuthError>}
            <Divider><span>로그인 방법 선택</span></Divider>
            <ProviderList>
              {providers.map(({ id, label, Icon }) => (
                <ProviderButton
                  key={id}
                  data-behavior={`login-${id}`}
                  type="button"
                  onClick={() => startProviderLogin(id)}
                  disabled={isPreparingLogin}
                >
                  <ProviderLogo aria-hidden="true"><Icon /></ProviderLogo>
                  <span>{label}</span>
                </ProviderButton>
              ))}
            </ProviderList>
            {/* 소셜 로그인 버튼이 곧 가입이므로 약관과 처리방침을 이 자리에서 알린다. 로그인 창 위에 띄워 대화 상태를 유지한다. */}
            <OAuthNotice>
              계속하면 <PolicyLink type="button" onClick={() => setOpenPolicy("terms")}>이용약관</PolicyLink>에
              동의하고 만 14세 이상임을 확인합니다. 개인정보는{" "}
              <PolicyLink type="button" $strong onClick={() => setOpenPolicy("privacy")}>개인정보 처리방침</PolicyLink>에
              따라 처리됩니다.
            </OAuthNotice>
          </OAuthCard>
        </Overlay>
      )}
      <ModalTermsOfService isOpen={openPolicy === "terms"} onClose={() => setOpenPolicy(null)} zIndex={3100} />
      <ModalPrivacyPolicy isOpen={openPolicy === "privacy"} onClose={() => setOpenPolicy(null)} zIndex={3100} />
    </>
  );
};

const Experience = styled.section`
  position: ${({ $isChatMode }) => ($isChatMode ? "fixed" : "relative")};
  inset: ${({ $isChatMode }) => ($isChatMode ? "0" : "auto")};
  z-index: ${({ $isChatMode }) => ($isChatMode ? "1000" : "auto")};
  width: 100%;
  min-height: ${({ $isChatMode }) => ($isChatMode ? "100dvh" : "0")};
  height: ${({ $isChatMode }) => ($isChatMode ? "100dvh" : "auto")};
  background: #fff;
  overflow: ${({ $isChatMode }) => ($isChatMode ? "hidden" : "visible")};
`;

const GuestHeader = styled.header`
  height: ${({ $mobile }) => ($mobile ? "76px" : "83px")};
  padding: 0;
  box-sizing: border-box;
  background: #fff;
  position: relative;
  z-index: 5;
  flex-shrink: 0;
`;

const LogoButton = styled.button`
  position: absolute;
  top: ${({ $mobile }) => ($mobile ? "25px" : "30px")};
  left: ${({ $mobile }) => ($mobile ? "24px" : "100px")};
  border: 0;
  padding: 0;
  background: transparent;
`;

const LogoImage = styled.img`
  display: block;
  width: 132px;
  height: auto;

  @media (max-width: 599px) {
    width: 114px;
  }
`;

const HeaderLoginGroup = styled.div`
  position: absolute;
  top: ${({ $mobile }) => ($mobile ? "19px" : "21px")};
  right: ${({ $mobile }) => ($mobile ? "24px" : "100px")};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderLoginButton = styled.button`
  width: 100px;
  height: 48px;
  border: 0;
  border-radius: 32px;
  background: linear-gradient(104deg, #6ef4ac 22%, #78a5fe 87%);
  color: rgba(0, 0, 0, 0.85);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 599px) {
    width: auto;
    height: 44px;
    padding: 0 20px;
    font-size: 14px;
  }
`;

const DesktopAskArea = styled.div`
  min-height: 0;
  padding-bottom: ${({ $isChatMode }) => ($isChatMode ? "0" : "32px")};
`;

const MobileAskArea = styled.div`
  min-height: 0;
  padding-top: ${({ $isChatMode }) => ($isChatMode ? "0" : "16px")};
  box-sizing: border-box;
`;

const MobileChatCloseButton = styled.button`
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 10px);
  left: calc(env(safe-area-inset-left, 0px) + 12px);
  z-index: 1010;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  cursor: pointer;

  svg {
    display: block;
    width: 100%;
    height: 100%;

    path {
      stroke: rgba(0, 0, 0, 0.72);
      stroke-width: 1.7;
      stroke-linecap: round;
    }
  }
`;

const DesktopStoreLinks = styled.nav`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 0 24px 20px;

  a,
  img {
    display: block;
    width: 140px;
    height: 48px;
  }
`;

const Overlay = styled(Modal).attrs({
  isOpen: true,
  preventScroll: true,
  style: { overlay: { backgroundColor: "transparent", zIndex: 3000 } },
})`
  --guest-safe-area-left: env(safe-area-inset-left, 0px);
  --guest-safe-area-right: env(safe-area-inset-right, 0px);

  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
  padding-top: max(24px, env(safe-area-inset-top, 0px));
  padding-right: max(
    24px,
    var(--guest-safe-area-left),
    var(--guest-safe-area-right)
  );
  padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
  padding-left: max(
    24px,
    var(--guest-safe-area-left),
    var(--guest-safe-area-right)
  );
  background: rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`;

const RequiredCard = styled.div`
  position: relative;
  flex: 0 0 auto;
  margin-block: auto;
  width: 400px;
  max-width: 100%;
  max-height: calc(100vh - 48px);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  border-radius: 24px;
  background: #fff;

  &::-webkit-scrollbar {
    display: none;
  }

  @supports (height: 100dvh) {
    max-height: calc(
      100dvh - max(24px, env(safe-area-inset-top, 0px)) -
        max(24px, env(safe-area-inset-bottom, 0px))
    );
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: 0;
  padding: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;

  svg {
    display: block;
    width: 100%;
    height: 100%;

    path {
      stroke: rgba(0, 0, 0, 0.72);
      stroke-width: 1.7;
      stroke-linecap: round;
    }
  }
`;

const LockImage = styled.img`
  display: block;
  width: 100%;
  height: 195px;
  object-fit: cover;
`;

const RequiredContent = styled.div`
  padding: 15px 32px 26px;
`;

const RequiredTitle = styled.h2`
  word-break: keep-all;
  margin: 0 0 10px;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: -0.4px;
`;

const RequiredDescription = styled.p`
  margin: 0 0 20px;
  color: rgba(0, 0, 0, 0.56);
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: -0.26px;
`;

const ContinueButton = styled.button`
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 8px;
  background: #062515;
  color: #00f370;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const OAuthCard = styled.div`
  position: relative;
  flex: 0 0 auto;
  margin-block: auto;
  width: 404px;
  max-width: 100%;
  max-height: calc(100vh - 48px);
  padding: 44px 32px 24px;
  border-radius: 24px;
  background: linear-gradient(155deg, #edfff5 0%, #fff 33%);
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @supports (height: 100dvh) {
    max-height: calc(
      100dvh - max(24px, env(safe-area-inset-top, 0px)) -
        max(24px, env(safe-area-inset-bottom, 0px))
    );
  }
`;

const OAuthTitle = styled.h2`
  margin: 0;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.48px;
`;

const OAuthDescription = styled.p`
  margin: 20px 0 28px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
`;

const OAuthError = styled.p`
  margin: -14px 0 18px;
  color: #c43b3b;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  color: rgba(0, 0, 0, 0.3);
  font-size: 12px;
  white-space: nowrap;

  &::before,
  &::after {
    content: "";
    height: 1px;
    flex: 1;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProviderButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 54px;
  padding: 0 44px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 100px;
  background: #fff;
  color: rgba(0, 0, 0, 0.88);
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.32px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #00c968;
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: #f9faf9;
    }
  }

  &:disabled {
    cursor: wait;
    opacity: 0.55;
  }
`;

const OAuthNotice = styled.p`
  margin: 18px 0 0;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  word-break: keep-all;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const PolicyLink = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.7);
  font: inherit;
  font-weight: ${({ $strong }) => ($strong ? 700 : 500)};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
`;

const ProviderLogo = styled.span`
  position: absolute;
  top: 50%;
  left: 14px;
  display: block;
  width: 22px;
  height: 22px;
  transform: translateY(-50%);

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export default GuestChatExperience;
